import { useState } from 'react';
import { Users, DollarSign, Pencil, Check, X, Shield, ShieldOff } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Empresa, Usuario, PAPEIS, formatUsd } from './types';

export interface UsuarioStats {
  personas: number;
  feedbacks: number;
  custo: number;
}

interface Props {
  usuarios: Usuario[];
  totalUsuarios: number;
  empresas: Empresa[];
  statsByUser: Record<string, UsuarioStats>;
  totalPersonas: number;
  onOpenCost: (usuarioId: string, nome: string) => void;
  onSaveUser: (
    usuarioId: string,
    patch: { nome: string; papel: Usuario['papel']; empresa_id: string | null }
  ) => Promise<void>;
}

export const UsuariosSummaryCard = ({
  usuarios,
  totalUsuarios,
  empresas,
  statsByUser,
  totalPersonas,
  onOpenCost,
  onSaveUser,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    nome: string;
    papel: Usuario['papel'];
    empresa_id: string | null;
  }>({ nome: '', papel: 'vendedor', empresa_id: null });
  const [roleTarget, setRoleTarget] = useState<Usuario | null>(null);
  const [savingRole, setSavingRole] = useState(false);

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditForm({ nome: '', papel: 'vendedor', empresa_id: null });
  };

  const empresaNome = (id: string | null) =>
    id ? empresas.find((e) => e.id === id)?.nome || null : null;

  const totalFeedbacksUsuarios = Object.values(statsByUser).reduce(
    (s, v) => s + v.feedbacks,
    0
  );
  const custoTotalUsuarios = Object.values(statsByUser).reduce((s, v) => s + v.custo, 0);
  const mediaPorUsuario = totalUsuarios > 0 ? custoTotalUsuarios / totalUsuarios : 0;

  return (
    <>
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setOpen(true)}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Usuários
          </CardTitle>
          <CardDescription>Clique para ver o detalhamento</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{totalUsuarios}</p>
          <p className="text-sm text-muted-foreground">usuários cadastrados</p>
          <p className="text-xs text-muted-foreground mt-2">
            {totalPersonas} personas · {totalFeedbacksUsuarios} feedbacks
          </p>
          <p className="text-xs text-muted-foreground">
            Custo médio por usuário: {formatUsd(mediaPorUsuario)}
          </p>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) cancelEditing();
        }}
      >
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usuários Cadastrados ({usuarios.length})
            </DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead className="text-right">Personas</TableHead>
                <TableHead className="text-right">Feedbacks</TableHead>
                <TableHead className="text-right">Custo (USD)</TableHead>
                <TableHead>Créditos</TableHead>
                <TableHead className="w-[120px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usuarios.map((usuario) => {
                const stats = statsByUser[usuario.id] || { personas: 0, feedbacks: 0, custo: 0 };
                if (editingUserId === usuario.id) {
                  return (
                    <TableRow key={usuario.id}>
                      <TableCell>
                        <Input
                          value={editForm.nome}
                          onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{usuario.email}</TableCell>
                      <TableCell>
                        <Select
                          value={editForm.papel}
                          onValueChange={(value: Usuario['papel']) =>
                            setEditForm({ ...editForm, papel: value })
                          }
                        >
                          <SelectTrigger className="h-8 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAPEIS.map((p) => (
                              <SelectItem key={p.value} value={p.value}>
                                {p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={editForm.empresa_id || 'none'}
                          onValueChange={(value) =>
                            setEditForm({
                              ...editForm,
                              empresa_id: value === 'none' ? null : value,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-40">
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem empresa</SelectItem>
                            {empresas.map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">{stats.personas}</TableCell>
                      <TableCell className="text-right">{stats.feedbacks}</TableCell>
                      <TableCell className="text-right">{formatUsd(stats.custo)}</TableCell>
                      <TableCell>{usuario.creditos}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={async () => {
                              await onSaveUser(usuario.id, {
                                nome: editForm.nome.trim(),
                                papel: editForm.papel,
                                empresa_id: editForm.empresa_id,
                              });
                              cancelEditing();
                            }}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={cancelEditing}>
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={usuario.id}>
                    <TableCell className="font-medium">{usuario.nome}</TableCell>
                    <TableCell className="text-muted-foreground">{usuario.email}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          usuario.papel === 'admin'
                            ? 'bg-primary/20 text-primary'
                            : usuario.papel === 'coordenador'
                              ? 'bg-blue-500/20 text-blue-600'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {PAPEIS.find((p) => p.value === usuario.papel)?.label || usuario.papel}
                      </span>
                    </TableCell>
                    <TableCell>
                      {usuario.empresa_id ? (
                        <span className="text-foreground">{empresaNome(usuario.empresa_id)}</span>
                      ) : (
                        <span className="text-amber-600 font-medium">Pendente</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{stats.personas}</TableCell>
                    <TableCell className="text-right">{stats.feedbacks}</TableCell>
                    <TableCell className="text-right">{formatUsd(stats.custo)}</TableCell>
                    <TableCell>{usuario.creditos}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onOpenCost(usuario.id, usuario.nome)}
                          title="Ver custos"
                        >
                          <DollarSign className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditingUserId(usuario.id);
                            setEditForm({
                              nome: usuario.nome,
                              papel: usuario.papel,
                              empresa_id: usuario.empresa_id,
                            });
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setRoleTarget(usuario)}
                          title={
                            usuario.papel === 'admin'
                              ? 'Remover admin (voltar para coordenador)'
                              : 'Tornar admin'
                          }
                        >
                          {usuario.papel === 'admin' ? (
                            <ShieldOff className="h-4 w-4 text-destructive" />
                          ) : (
                            <Shield className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {usuarios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                    Nenhum usuário cadastrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!roleTarget} onOpenChange={(o) => !o && setRoleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleTarget?.papel === 'admin' ? 'Remover acesso admin' : 'Tornar administrador'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleTarget?.papel === 'admin'
                ? `${roleTarget?.nome} (${roleTarget?.email}) voltará a ser coordenador e perderá o acesso ao painel administrativo.`
                : `${roleTarget?.nome} (${roleTarget?.email}) terá acesso total ao painel administrativo da plataforma.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingRole}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={savingRole}
              onClick={async (e) => {
                e.preventDefault();
                if (!roleTarget) return;
                setSavingRole(true);
                await onSaveUser(roleTarget.id, {
                  nome: roleTarget.nome,
                  papel: roleTarget.papel === 'admin' ? 'coordenador' : 'admin',
                  empresa_id: roleTarget.empresa_id,
                });
                setSavingRole(false);
                setRoleTarget(null);
              }}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
